// src/components/SummaryTable.js
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import { lightColours, darkColours } from '../../../styles/colours';
import { alpha } from '@mui/material/styles';


const SummaryTable = ({ results }) => {
  // If no results provided, don't render the table.
  if (!results || Object.keys(results).length === 0) return null;

  // Destructure the base values.
  const { totalGrossIncome, taxableIncome, taxPaid, nationalInsurance, ni2029Surcharge, takeHome } = results;

  // A helper to format numbers as currency.
  const formatCurrency = (value) =>
    `£${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Build a values object.
  // Note: National Insurance total includes the 2029 surcharge
  const totalNI = nationalInsurance + (ni2029Surcharge || 0);
  const values = {
    'Gross Income': totalGrossIncome,
    'Taxable Income': taxableIncome,
    'Tax': taxPaid,
    'National Insurance': totalNI,
    'Net Take-Home Pay': takeHome,
  };

  // Add optional rows if they exist.
  if (results.overtimeAddition !== undefined && Number(results.overtimeAddition) > 0) {
    values['Overtime Addition'] = results.overtimeAddition;
  }
  if (results.studentLoanRepayment !== undefined && Number(results.studentLoanRepayment) > 0) {
    values['Student Loan Repayments'] = results.studentLoanRepayment;
  }
  if (results.pensionDeduction !== undefined && Number(results.pensionDeduction) > 0) {
    if (results.personalPension !== undefined) {
      values['Pension Contributions (incl. basic rate top up)'] = results.pensionDeduction;
    } else {
      values['Pension Contributions'] = results.pensionDeduction;
    }
  }
  // Add 2029 NI Surcharge row if applicable
  if (ni2029Surcharge !== undefined && Number(ni2029Surcharge) > 0) {
    values['2029 NI Surcharge'] = ni2029Surcharge;
  }

  // Define the desired ordering.
  const orderedLabels = [
    'Gross Income',
    'Overtime Addition',
    'Taxable Income',
    'Tax',
    'National Insurance',
    '2029 NI Surcharge',
    'Pension', // We'll treat any pension row as this.
    'Student Loan Repayments',
    'Net Take-Home Pay'
  ];

  // Build an array of rows in the specified order.
  const rows = [];
  orderedLabels.forEach(label => {
    if (label === 'Pension') {
      if (values['Pension Contributions (incl. basic rate top up)'] !== undefined) {
        rows.push({
          label: 'Pension Contributions (incl. basic rate top up)',
          value: values['Pension Contributions (incl. basic rate top up)']
        });
      } else if (values['Pension Contributions'] !== undefined) {
        rows.push({
          label: 'Pension Contributions',
          value: values['Pension Contributions']
        });
      }
    } else if (values[label] !== undefined) {
      rows.push({
        label,
        value: values[label]
      });
    }
  });

  // Helper: Convert a yearly value into yearly, monthly, weekly, and daily values.
  const convertValues = (yearly) => ({
    yearly,
    monthly: yearly / 12,
    weekly: yearly / 52,
    daily: yearly / 365,
  });

  // Define sample left border styles for each row label.
  // For rows that don't have a specified style, no border is applied.
  const rowStyles = {
    'Gross Income': { borderLeft: `10px solid white` },
    'Overtime Addition': { borderLeft: `10px solid white` },
    'Taxable Income': { borderLeft: `10px solid white` },
    'Tax': { borderLeft: `10px solid ${lightColours.darkBlue}` },
    'National Insurance': { borderLeft: `10px solid ${darkColours.darkPink}` },
    '2029 NI Surcharge': { borderLeft: `10px solid ${darkColours.darkPink}` },
    'Pension': { borderLeft: `10px solid white` },
    'Student Loan Repayments': { borderLeft: `10px solid white` },
    'Net Take-Home Pay': { borderLeft: `10px solid ${lightColours.midBlue}` },
  };  

  const finalRows = rows.map(row => {
    const converted = convertValues(row.value);
    // Determine if this row is a deduction.
    const isDeduction =
    (row.label.includes('Tax') && !row.label.includes('Taxable Income')) ||
    row.label.includes('National Insurance') ||
    row.label.includes('2029 NI Surcharge') ||
    row.label.includes('Student Loan') ||
    row.label.includes('Pension');
  
    // For styling, map any pension row to the 'Pension' key.
    const styleKey = row.label.includes('Pension') ? 'Pension' : row.label;
    const rowBorderStyle = rowStyles[styleKey] || {};
  
    // If it's a deduction, merge in a lightGrey background.
    const combinedStyle = isDeduction
      ? { ...rowBorderStyle, backgroundColor: alpha(lightColours.lightGrey, 0.5)}
      : rowBorderStyle;
  
    return {
      label: row.label,
      yearly: isDeduction ? `-${formatCurrency(converted.yearly)}` : formatCurrency(converted.yearly),
      monthly: isDeduction ? `-${formatCurrency(converted.monthly)}` : formatCurrency(converted.monthly),
      weekly: isDeduction ? `-${formatCurrency(converted.weekly)}` : formatCurrency(converted.weekly),
      daily: isDeduction ? `-${formatCurrency(converted.daily)}` : formatCurrency(converted.daily),
      rowBorderStyle: combinedStyle,
    };
  });
  

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        mt: 3,
        overflowX: 'auto', // Enables horizontal scrolling on smaller viewports.
        border: 'none',
        boxShadow: 'none',
        borderRadius: 0
      }}
    >
      <Typography variant="h6" sx={{ m: 2, fontSize: '1.5rem', fontWeight:'bold' }}>
        Summary
      </Typography>
      <Table sx={{ minWidth: 500 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Item</TableCell>
            <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Annual</TableCell>
            <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Monthly</TableCell>
            <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Weekly</TableCell>
            <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Daily</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
  {finalRows.map((row, index) => {
    // Check if the row should be bold.
    const isBold = row.label === 'Net Take-Home Pay' || row.label === 'Gross Income';
    return (
      <TableRow key={index} sx={row.rowBorderStyle}>
        <TableCell sx={{ fontSize: '1rem', fontWeight: isBold ? 'bold' : 'normal' }}>
          {row.label}
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontSize: '1rem', fontWeight: isBold ? 'bold' : 'normal' }}
        >
          {row.yearly}
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontSize: '1rem', fontWeight: isBold ? 'bold' : 'normal' }}
        >
          {row.monthly}
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontSize: '1rem', fontWeight: isBold ? 'bold' : 'normal' }}
        >
          {row.weekly}
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontSize: '1rem', fontWeight: isBold ? 'bold' : 'normal' }}
        >
          {row.daily}
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>
      </Table>
    </TableContainer>
  );
};

export default SummaryTable;
